import { AbstractSession, AbstractUser, StripeProduct } from "@kvix/shared";
import { KvixIcon } from "@kvix/ui";
import { CircularProgress, Fade, makeStyles, Theme } from "@material-ui/core";
import React from "react";
import { useVideoThumbnail } from "../../../hooks/videoThumbnail";
import { DefaultFlow, NewFlow } from "../../PurchaseFlow";
import { Guard } from "../Guard";
import Cookies from "js-cookie";

interface Props {
  session: AbstractSession;
  fallbackToPoster?: boolean;
  rightOffset?: number;
  withBlur?: boolean;
  user: AbstractUser;
}

const useStyles = makeStyles<Theme, Props>((theme) => ({
  root: {
    overflow: "hidden",
    width: "100%",
    paddingTop: "56.25%",
    display: "block",

    position: "relative",
    background: "#222",

    boxShadow: "0 6px 16px -8px rgba(0, 0, 0, 0.2)",

    [theme.breakpoints.down(800)]: {
      width: "calc(100% + 64px)",
      marginLeft: -32,
      marginRight: -32,
    },
  },
  overlay: {
    cursor: "pointer",
    position: "absolute",
    top: 0,
    left: 0,
    right: (props) => props.rightOffset || 0,
    bottom: 0,

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  image: {
    display: "block",
    position: "absolute",
    top: 0,
    left: 0,
    right: (props) => props.rightOffset || 0,
    bottom: 0,
    filter: (props) => (props.withBlur ? "blur(18px)" : "none"),
    transform: (props) => (props.withBlur ? "scale(1.1)" : "scale(1)"),

    pointerEvents: "none",

    "&:before": {
      content: "''",
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
  },
  play: {
    padding: 10,
    fontSize: 70,

    color: theme.palette.common.white,
    background: theme.palette.primary.main,
    boxShadow: "0px 0px 24px rgba(0, 0, 0, 0.3)",
    borderRadius: "50%",

    transition: "all 0.125s ease-out",
  },
}));

export const PremiumVideoPlaceholderPoster: React.FC<Props> = (props) => {
  const classes = useStyles(props);
  const { session } = props;
  const [uri, loading] = useVideoThumbnail(session, {
    fallbackToPoster: props.fallbackToPoster,
  });

  return (
    <span className={classes.root}>
      <Fade in={!loading} timeout={1000}>
        <span
          className={classes.image}
          style={{
            backgroundImage: `url(${uri})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      </Fade>
      <Guard.Click
        FlowComponent={
          session?.requiredProductAnytime?.includes(StripeProduct.LIVE) ? (
            <NewFlow registerProps={{ checkoutSource:  Cookies.get('wp-campaign') ? 'wp' : 'kvix' }} currentTab={props.user ? 1 : 0} />
          ) : (
            <DefaultFlow registerProps={{ hidePrice: true }} />
          )
        }
      >
        <span className={classes.overlay}>
          <KvixIcon.Play className={classes.play} color="primary" />
        </span>
      </Guard.Click>

      <Fade in={loading}>
        <span className={classes.overlay}>
          <CircularProgress size={38} thickness={4} />
        </span>
      </Fade>
    </span>
  );
};
